/**
 * Job applications (vacancies) of the international (Philippines) recruitment.
 * Client-safe: no DB access.
 *
 * Each vacancy has its own shared intake link, its own application form and
 * its own assessment; the Philippines page filters everything by vacancy.
 *   agent       - Remote Customer Service Representative (the original form,
 *                 src/lib/recruitment/intl-application.ts). Client Care (PH)
 *                 candidates can be accepted from this application too.
 *   client_care - Client Care Associate: the agent form under its own link,
 *                 inbox and wording.
 *   marketing   - Marketing Operations & AI Research Specialist
 *                 (src/lib/recruitment/marketing-application.ts).
 *
 * Adding a vacancy: add it here, give it a form definition, and add the value
 * to the two CHECK constraints (recruitment_intl_applications.vacancy,
 * recruitment_intake_link.vacancy).
 */
import { PH_DEFAULT_POSITION } from '@/lib/recruitment/international';
import { isPhilippinesPositionName } from '@/lib/philippines-payroll';
import {
  CLIENT_CARE_APPLICATION_POSITIONS,
  PH_APPLICATION_POSITIONS,
  isClientCarePositionName,
} from '@/lib/recruitment/client-care-recruitment';

export const INTL_VACANCIES = ['agent', 'client_care', 'marketing'] as const;
export type IntlVacancy = (typeof INTL_VACANCIES)[number];

/** Vacancies answered with the agent form (intl-application.ts). */
export type AgentFormVacancy = Exclude<IntlVacancy, 'marketing'>;

export const DEFAULT_INTL_VACANCY: IntlVacancy = 'agent';

export function parseIntlVacancy(value: unknown): IntlVacancy | null {
  return typeof value === 'string' && (INTL_VACANCIES as readonly string[]).includes(value)
    ? (value as IntlVacancy)
    : null;
}

/** Position HR accepts a Marketing application for. "(PH)" = Philippines payroll. */
export const MARKETING_PH_POSITION = 'Marketing Operations & AI Research Specialist (PH)';

export function isMarketingPositionName(name: string | null | undefined): boolean {
  return /marketing/i.test(name ?? '');
}

/** Postgres regex (case-insensitive match with ~*) equivalent of isMarketingPositionName. */
export const MARKETING_POSITION_SQL_PATTERN = 'marketing';

/** Postgres regex (case-insensitive match with ~*) equivalent of isClientCarePositionName. */
export const CLIENT_CARE_POSITION_SQL_PATTERN = 'client\\s*care';

/** Vacancy a candidate belongs to, from the position they applied for. */
export function vacancyForPosition(positionApplied: string | null | undefined): IntlVacancy {
  if (isMarketingPositionName(positionApplied)) return 'marketing';
  return isClientCarePositionName(positionApplied) ? 'client_care' : 'agent';
}

/** SQL equivalent of vacancyForPosition for a position expression. */
export function vacancyForPositionSql(positionExpr: string): string {
  return `(CASE
    WHEN ${positionExpr} ~* '${MARKETING_POSITION_SQL_PATTERN}' THEN 'marketing'
    WHEN ${positionExpr} ~* '${CLIENT_CARE_POSITION_SQL_PATTERN}' THEN 'client_care'
    ELSE 'agent' END)`;
}

export type IntlVacancyPosition = { value: string; role: string };

export type IntlVacancyInfo = {
  /** Short name in the HR selector. */
  label: string;
  labelEs: string;
  /** Job title on the public pages. */
  jobTitle: string;
  /** Line under the job title / in the public header. */
  tagline: string;
  /** Landing page paragraph. */
  intro: string;
  /** Landing page "What to have ready". */
  haveReady: string[];
  /** Positions HR can accept an application of this vacancy for (first = default). */
  positions: readonly IntlVacancyPosition[];
  /** Recordings of the audio step (inbox shows n/total). */
  audioParts: number;
  /** The application includes the 1-minute typing test. */
  hasTypingTest: boolean;
};

export const INTL_VACANCY_INFO: Record<IntlVacancy, IntlVacancyInfo> = {
  agent: {
    label: 'Agent',
    labelEs: 'Agente',
    jobTitle: 'Remote Customer Service Representative',
    tagline: '100% Remote · Independent Contractor',
    intro:
      'Join TELI and support US-based customers from home. The application takes about 20–30 minutes and includes a short recorded voice assessment.',
    haveReady: [
      'A government-issued ID and a selfie holding it',
      'A screenshot of your computer specifications (OS, processor, and RAM)',
      'A screenshot of an internet speed test (speedtest.net)',
      'Your resume / CV as a PDF or Word file',
      'A quiet space and a working microphone for a short recorded voice assessment',
    ],
    positions: PH_APPLICATION_POSITIONS,
    audioParts: 3,
    hasTypingTest: true,
  },
  client_care: {
    label: 'Client Care',
    labelEs: 'Client Care',
    jobTitle: 'Client Care Associate',
    tagline: '100% Remote · Independent Contractor',
    intro:
      'Join TELI and support our US-based clients from home. The application takes about 20–30 minutes and includes a short recorded voice assessment.',
    haveReady: [
      'A government-issued ID and a selfie holding it',
      'A screenshot of your computer specifications (OS, processor, and RAM)',
      'A screenshot of an internet speed test (speedtest.net)',
      'Your resume / CV as a PDF or Word file',
      'A quiet space and a working microphone for a short recorded voice assessment',
    ],
    positions: CLIENT_CARE_APPLICATION_POSITIONS,
    audioParts: 3,
    hasTypingTest: true,
  },
  marketing: {
    label: 'Marketing',
    labelEs: 'Marketing',
    jobTitle: 'Marketing Operations & AI Research Specialist',
    tagline: 'Remote · Philippines',
    intro:
      'Thank you for your interest in working with TELI. Please complete this application honestly and carefully. Advanced spoken and written English is required. Answer the experience, skills, and assessment questions in English. Shortlisted applicants will be contacted regarding the next stage.',
    haveReady: [
      'Your CV as a PDF or Word file',
      'A recent photograph of yourself',
      'A government-issued ID and a selfie holding it',
      'A screenshot of your computer specifications (OS, processor, and RAM)',
      'A screenshot of a recent internet speed test',
      'A quiet space and a working microphone for a short recorded answer',
    ],
    positions: [{ value: MARKETING_PH_POSITION, role: 'Marketing' }],
    audioParts: 1,
    hasTypingTest: false,
  },
};

/**
 * Job applications a submission of `vacancy` can sit in (itself included):
 * the ones answered with the same form, so every answer, upload and recording
 * still reads the same after a move.
 */
export function movableVacancies(vacancy: IntlVacancy): IntlVacancy[] {
  return INTL_VACANCIES.filter((v) => (v === 'marketing') === (vacancy === 'marketing'));
}

export function defaultPositionForVacancy(vacancy: IntlVacancy): string {
  return INTL_VACANCY_INFO[vacancy].positions[0]?.value ?? PH_DEFAULT_POSITION;
}

/**
 * Assessment a candidate takes once in the funnel:
 *   customer_service - the 4-phase psychometric test (psychometric-test.ts)
 *   general          - the General applied cognitive and behavioral assessment
 *                      (general-assessment.ts)
 * Marketing (Jesus 2026-10-01) and Client Care (PH) (Jesus 2026-10-07: "make
 * the psychometric exam for client care position the same exam that we made
 * for marketing"); agents and the Mexico Client Care candidates keep the
 * customer service test. A Client Care (PH) candidate who already completed
 * the customer service test before 2026-10-07 is still accepted at Stage 1
 * on that result (interviews route + Recruit page).
 */
export type AssessmentKind = 'customer_service' | 'general';

export function assessmentKindForPosition(
  positionApplied: string | null | undefined,
): AssessmentKind {
  if (isMarketingPositionName(positionApplied)) return 'general';
  return isClientCarePositionName(positionApplied) && isPhilippinesPositionName(positionApplied)
    ? 'general'
    : 'customer_service';
}

/**
 * Positions whose interviewer can be ANY active non-agent employee (Jesus
 * 2026-10-01: "any non agent role employee ... for future scenarios as well").
 * Agent and Client Care positions keep their fixed interviewer lists.
 */
export function isOpenInterviewerPosition(positionApplied: string | null | undefined): boolean {
  return isMarketingPositionName(positionApplied);
}
